/**
 * Studio bearer-token store for /new.
 *
 * The studio session cookie is a THIRD-PARTY cookie from dreambooth.app's point
 * of view, so it is blocked in Incognito / Safari ITP / post-3P-cookie Chrome —
 * which is why cross-origin session polling "never appears signed in". Instead
 * the sign-in popup hands us the studio's NextAuth JWT (via postMessage); we
 * keep it here and send it as `Authorization: Bearer` on studio API calls, which
 * `utils/resolveAuthSession.ts` already accepts. The JWT is encrypted (JWE), so
 * we can't read the user from it client-side — the popup sends the user +
 * expiry alongside it for display.
 */

import type { StudioSessionUser } from './onboardingApi'

export interface StoredStudioAuth {
  token: string
  user: StudioSessionUser
  /** Epoch ms when the studio session expires. */
  expiresAt: number
}

const STORAGE_KEY = 'db.studioAuth'

let cached: StoredStudioAuth | null | undefined

function read(): StoredStudioAuth | null {
  if (cached !== undefined) return cached
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    cached = raw ? (JSON.parse(raw) as StoredStudioAuth) : null
  } catch {
    cached = null
  }
  return cached
}

/** The stored auth if present and not expired; clears + returns null if stale. */
export function getStoredAuth(): StoredStudioAuth | null {
  const a = read()
  if (!a || !a.token || !a.user?.email) return null
  if (typeof a.expiresAt === 'number' && a.expiresAt <= Date.now()) {
    clearStudioAuth()
    return null
  }
  return a
}

export function getStudioToken(): string | null {
  return getStoredAuth()?.token ?? null
}

export function setStudioAuth(auth: StoredStudioAuth): void {
  cached = auth
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(auth))
  } catch {
    // storage full / disabled — the in-memory copy still serves this session
  }
}

export function clearStudioAuth(): void {
  cached = null
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}
